import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {Loader, PackageCheck, PackageX, RotateCcw, Ticket, X} from "lucide-react";
import Button from "../Common/Button";
import {
    SearchInput, ToolbarDate, ToolbarSelect, ListLoader, ListEmpty, ListCard, thClass, mobileListClass, mobileRowClass, Pagination
} from "../../stock/StockUI";
import {
    SALE_TYPE_LABELS,
    CREDIT_NOTE_STATUS_CLASSES,
    CREDIT_NOTE_STATUS_LABELS,
    fetchEmployeeOptions,
    fetchReturn,
    fetchReturnReasons,
    fetchReturns,
    formatDate,
    formatDateTime,
    formatMoney,
    toDayString,
} from "./returnsApi";

const PAGE_SIZE = 20;

export const TYPE_CLASSES = {
    return: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400",
    exchange: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
};

const TypeBadge = ({type}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${TYPE_CLASSES[type]}`}>
        {SALE_TYPE_LABELS[type]}
    </span>
);

export const defaultPeriod = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return {from: toDayString(from), to: toDayString(to)};
};

// Refund lines are negative payments (money given back) — show them positive.
const refundsSummary = (refunds = []) =>
    refunds
        .filter((r) => r.amount < 0)
        .map((r) => `${r.name} ${formatMoney(Math.abs(r.amount))}`)
        .join(" · ") || "—";

// ─── Détail ──────────────────────────────────────────────────────────────────

const ReturnDetailModal = ({id, onClose}) => {
    const {data, isLoading, isError} = useQuery({
        queryKey: ["return", id],
        queryFn: () => fetchReturn(id),
    });

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const returned = data?.items?.filter((i) => i.is_return) || [];
    const added = data?.items?.filter((i) => !i.is_return) || [];

    // Portal: an animated (transformed) page ancestor would otherwise become the
    // containing block of `fixed` and leave part of the screen uncovered.
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {data ? `${SALE_TYPE_LABELS[data.sale_type]} #${data.reference}` : "Retour"}
                        </h3>
                        {data && (
                            <p className="text-xs text-gray-500">
                                {formatDateTime(data.created_at)} · {data.employee || "—"}
                                {data.device_name ? ` · ${data.device_name}` : ""}
                            </p>
                        )}
                    </div>
                    <Button variant="ghost" size="sm" icon={X} title="Fermer" onClick={onClose}/>
                </div>

                <div className="overflow-y-auto p-5 space-y-5">
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Loader className="w-4 h-4 animate-spin"/> Chargement…
                        </div>
                    ) : isError ? (
                        <p className="text-sm text-red-500">Impossible de charger ce retour.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                                    <div className="text-xs text-gray-500">Articles retournés</div>
                                    <div className="font-semibold text-rose-600">{formatMoney(data.returned_total)}</div>
                                </div>
                                {data.sale_type === "exchange" && (
                                    <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                                        <div className="text-xs text-gray-500">Nouveaux articles</div>
                                        <div className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(data.new_total)}</div>
                                    </div>
                                )}
                                <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
                                    <div className="text-xs text-gray-500">
                                        {data.total < 0 ? "Rendu au client" : "Payé par le client"}
                                    </div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(Math.abs(data.total))}</div>
                                </div>
                            </div>

                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
                                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                    <Ticket className="w-3.5 h-3.5"/> Ticket d'origine
                                </div>
                                {data.original_sale ? (
                                    <div className="text-gray-900 dark:text-gray-100">
                                        <span className="font-mono">#{data.original_sale.reference}</span>
                                        {data.original_sale.created_at && <> · {formatDateTime(data.original_sale.created_at)}</>}
                                        {data.original_sale.total != null && <> · {formatMoney(data.original_sale.total)}</>}
                                    </div>
                                ) : (
                                    <div className="text-amber-700 dark:text-amber-400">Retour sans ticket</div>
                                )}
                                {data.client && <div className="text-gray-600 dark:text-gray-300 mt-1">Client : {data.client}</div>}
                            </div>

                            <ItemsTable title="Articles retournés" items={returned} showReturnInfo/>
                            {added.length > 0 && <ItemsTable title="Nouveaux articles" items={added}/>}

                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Remboursement / règlement</h4>
                                {data.refunds.length === 0 ? (
                                    <p className="text-sm text-gray-500">Aucun paiement enregistré.</p>
                                ) : (
                                    <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                                        {data.refunds.map((r) => (
                                            <li key={r.id} className="flex justify-between px-3 py-2">
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {r.name} {r.amount < 0 ? "(rendu)" : "(encaissé)"}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(Math.abs(r.amount))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {data.credit_notes.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Avoirs émis</h4>
                                    <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                                        {data.credit_notes.map((n) => (
                                            <li key={n.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                                                <span className="font-mono text-gray-900 dark:text-gray-100">{n.code}</span>
                                                <span className="text-gray-600 dark:text-gray-300">
                                                    {formatMoney(n.amount)} · expire {n.expires_at ? formatDate(n.expires_at) : "jamais"}
                                                </span>
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${CREDIT_NOTE_STATUS_CLASSES[n.status]}`}>
                                                    {CREDIT_NOTE_STATUS_LABELS[n.status]}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.note && <p className="text-xs text-gray-500">Note : {data.note}</p>}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

const ItemsTable = ({title, items, showReturnInfo = false}) => (
    <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h4>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((item) => (
                    <tr key={item.id}>
                        <td className="px-3 py-2">
                            <div className="text-gray-900 dark:text-gray-100">{item.name}</div>
                            {showReturnInfo && (
                                <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-gray-500">
                                    {item.reason && <span>{item.reason}</span>}
                                    {item.restock ? (
                                        <span className="inline-flex items-center gap-1 text-green-600"><PackageCheck className="w-3.5 h-3.5"/> Remis en stock</span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-amber-600"><PackageX className="w-3.5 h-3.5"/> Non revendable</span>
                                    )}
                                </div>
                            )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {item.quantity} × {formatMoney(Math.abs(item.unit_price))}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {formatMoney(Math.abs(item.total))}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    </div>
);

// ─── Liste ───────────────────────────────────────────────────────────────────

const ReturnsListTab = () => {
    const [period, setPeriod] = useState(defaultPeriod);
    const [type, setType] = useState("");
    const [reasonId, setReasonId] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const {data: reasons = []} = useQuery({queryKey: ["returns-reasons"], queryFn: fetchReturnReasons});
    const {data: employees = []} = useQuery({queryKey: ["returns-employee-options"], queryFn: fetchEmployeeOptions});

    const filters = {
        from: period.from,
        to: period.to,
        type,
        reason_id: reasonId,
        employee_id: employeeId,
        search: debouncedSearch,
    };
    const {data, isLoading, isError, isFetching} = useQuery({
        queryKey: ["returns", filters, page],
        queryFn: () => fetchReturns({...filters, page, itemsPerPage: PAGE_SIZE}),
        placeholderData: keepPreviousData,
    });

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const resetPage = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    return (
        <div className="max-w-full">
            {/* Toolbar — search + period, then the filters */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={search} onChange={setSearch} placeholder="N° de ticket (retour ou origine)..."/>
                <div className="flex">
                    <ToolbarDate label="Du" value={period.from} max={period.to} className="flex-1 sm:w-44"
                                 onChange={(v) => { setPeriod((p) => ({...p, from: v})); setPage(1); }}/>
                    <ToolbarDate label="Au" value={period.to} min={period.from} className="flex-1 sm:w-44"
                                 onChange={(v) => { setPeriod((p) => ({...p, to: v})); setPage(1); }}/>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row">
                <ToolbarSelect label="Type" value={type} onChange={resetPage(setType)} className="sm:flex-1">
                    <option value="">Tous les types</option>
                    <option value="return">Retours</option>
                    <option value="exchange">Échanges</option>
                </ToolbarSelect>
                <ToolbarSelect label="Motif" value={reasonId} onChange={resetPage(setReasonId)} className="sm:flex-1">
                    <option value="">Tous les motifs</option>
                    {reasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </ToolbarSelect>
                {employees.length > 0 && (
                    <ToolbarSelect label="Employé" value={employeeId} onChange={resetPage(setEmployeeId)} className="sm:flex-1">
                        <option value="">Tous les employés</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </ToolbarSelect>
                )}
            </div>

            {isLoading ? (
                <ListLoader label="Chargement des retours..."/>
            ) : (
                <ListCard>
                    {isError ? (
                        <p className="text-sm text-red-500 p-6 text-center">Impossible de charger les retours.</p>
                    ) : items.length === 0 ? (
                        <ListEmpty icon={RotateCcw} title="Aucun retour ni échange"
                                   text="Aucune opération sur cette période. Modifiez la période ou les filtres."/>
                    ) : (<div className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
                        {/* Desktop Table */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Date</th>
                                    <th className={thClass}>Type</th>
                                    <th className={thClass}>Ticket d'origine</th>
                                    <th className={`${thClass} text-right`}>Retourné</th>
                                    <th className={thClass}>Motifs</th>
                                    <th className={thClass}>Remboursement</th>
                                    <th className={thClass}>Employé</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {items.map((r) => (
                                    <tr key={r.id} onClick={() => setSelectedId(r.id)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatDateTime(r.created_at)}</div>
                                            <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 font-mono">#{r.reference}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap"><TypeBadge type={r.sale_type}/></td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            {r.original_sale
                                                ? <span className="font-mono text-gray-700 dark:text-gray-300">#{r.original_sale.reference}</span>
                                                : <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">Sans ticket</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm font-semibold text-rose-600 dark:text-rose-400 whitespace-nowrap">
                                            {formatMoney(r.returned_total)}
                                            {r.sale_type === "exchange" && (
                                                <div className="text-xs font-normal text-gray-500 dark:text-gray-400">+ {formatMoney(r.new_total)} neufs</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{r.reasons.join(", ") || "—"}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{refundsSummary(r.refunds)}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.employee || "—"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                        <div className={mobileListClass}>
                            {items.map((r) => (
                                <div key={r.id} onClick={() => setSelectedId(r.id)} className={`${mobileRowClass} cursor-pointer`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-base font-semibold text-rose-600 dark:text-rose-400">{formatMoney(r.returned_total)}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {formatDateTime(r.created_at)} · <span className="font-mono">#{r.reference}</span>
                                            </p>
                                        </div>
                                        <TypeBadge type={r.sale_type}/>
                                    </div>
                                    <div className="text-sm mt-3 space-y-0.5 text-gray-500 dark:text-gray-400">
                                        <p>
                                            <span className="font-medium">Ticket :</span>{" "}
                                            {r.original_sale ? <span className="font-mono">#{r.original_sale.reference}</span> : "Sans ticket"}
                                        </p>
                                        {r.reasons.length > 0 && <p><span className="font-medium">Motifs :</span> {r.reasons.join(", ")}</p>}
                                        <p><span className="font-medium">Remboursement :</span> {refundsSummary(r.refunds)}</p>
                                        <p><span className="font-medium">Employé :</span> {r.employee || "—"}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>)}
                </ListCard>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <Pagination page={page} totalPages={totalPages} onPage={setPage} summary={`${total} opération(s)`}/>
            )}

            {selectedId && <ReturnDetailModal id={selectedId} onClose={() => setSelectedId(null)}/>}
        </div>
    );
};

export default ReturnsListTab;
